import React, {
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useContext,
  useEffect,
  useState,
} from 'react';
import LineChart from '@/pages/intelligence/components/Chart/components/LineChart';
import BarChart from '@/pages/intelligence/components/Chart/components/BarChart';
import PieChart from '@/pages/intelligence/components/Chart/components/PieChart';
import PieChartHover from '@/pages/intelligence/components/Chart/components/PieChartHover';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import { useIntl } from 'umi';
import { ChartContext } from '@/pages/intelligence/layouts';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';

const PUBLIC_HEIGHT = 350;

export interface IChartsIndexInstance {
  getBarInstance: () => any;
  getPieInstance: () => any;
  getLineInstance: () => any;
}

interface IChartsIndexProps {
  chartLoading?: boolean;
  isError?: boolean;
  isNodata?: boolean;
  echartHight?: number;
  chartHeight?: number;
  chartType?: string;
  barOption?: any;
  lineOption?: any;
  pieOption?: any;
  showBarPeiName?: any;
  barLengend?: any;
  // 上游传递ref，用于获取chart的示例，实现一些交互逻辑
  // lineChartRef,
  nodataMsg?: React.ReactNode;
  onChartEarliestClick?: () => void;
  isHoverPie?: boolean;
  pieData?: any;
  metricText?: string;
  startDate?: string;
  endDate?: string;
  topChartsType?: string;
  byDevice?: boolean;
  ifLineChartLegendOn?: boolean;
  secondTitleRight?: string;
  pieDataHighIndex?: number;
  dataSourceNames?: any;
  ifHasOtherHeadContent?: boolean;
}

// 可视化统一组件,获取到数据，根据不同类型切换echart
const ChartsIndex: React.ForwardRefRenderFunction<
  IChartsIndexInstance,
  IChartsIndexProps
> = (props, ref) => {
  const { formatMessage } = useIntl(); // 中英切换方法
  const {
    chartLoading,
    isError,
    isNodata,
    echartHight = 400,
    chartHeight,
    chartType = 'bar',
    barOption,
    lineOption,
    pieOption,
    showBarPeiName,
    barLengend,
    // 上游传递ref，用于获取chart的示例，实现一些交互逻辑
    // lineChartRef,
    nodataMsg = formatMessage({ id: 'common_nodata' }),
    onChartEarliestClick,
    isHoverPie = false,
    pieData,
    metricText = '',
    startDate,
    endDate,
    topChartsType,
    byDevice = false,
    ifLineChartLegendOn = false,
    secondTitleRight,
    pieDataHighIndex,
    dataSourceNames,
    ifHasOtherHeadContent = false,
  } = props;
  // console.debug('ChartsIndex', props);

  const height = chartHeight || PUBLIC_HEIGHT;

  const barChart = useRef<any>(null);
  const pieChart = useRef<any>(null);
  const lineChart = useRef<any>(null);

  // const lineChartOptionRef = useRef<any>(null);
  // 根据ChartContext消费者，获取setStatsChartOption方法
  const { setStatsChartOption, ifResetLegend } = useContext(ChartContext);

  const getNoSelectedLegend = (lineOption: any) => {
    const arrTemp = [];
    if (typeof lineOption === 'object' && lineOption !== null) {
      if (Object.prototype.hasOwnProperty.call(lineOption, 'legend')) {
        const selectedObj = lineOption?.legend?.selected || {};
        for (const key in selectedObj) {
          if (selectedObj[key] === false) {
            arrTemp.push(key);
          }
        }
      }
    }
    return arrTemp;
  };

  const [legendSelectedFalse, setLegendSelectedFalse] = useState(
    getNoSelectedLegend(lineOption),
  );

  const tabsChartArr: any = useMemo(() => {
    const tabsChart = {
      bar: {
        id: 'chartBar',
        component: (
          <>
            <BarChart
              ref={barChart}
              height={height}
              // colorArr={BAR_COLORS}
              // legendData={legendData}
              // xAxisData={xAxisData}
              // series={barSeries}
              // formatterFunc={chartConfig.barFormatter}
              // setOtherConfigFunc={chartConfig.setOtherConfigFunc}
              barOption={barOption}
              showBarPeiName={showBarPeiName}
              chartType={chartType}
              barLengend={barLengend}
              dataSourceNames={dataSourceNames}
            />
          </>
        ),
        disabled: false,
      },
      line: {
        id: 'chartLine',
        component: (
          <LineChart
            // ref={lineChartRef}
            ref={lineChart}
            height={height}
            // colorArr={BAR_COLORS}
            // legendData={legendData}
            // xAxisData={xAxisData}
            // series={lineSeries}
            // formatterFunc={chartConfig?.barFormatter}
            // setOtherConfigFunc={chartConfig?.setOtherConfigFunc}
            lineOption={lineOption}
            onChartEarliestClick={onChartEarliestClick}
            ifLineChartLegendOn={ifLineChartLegendOn}
            chartType={chartType}
            setLegendSelectedFalse={setLegendSelectedFalse}
          />
        ),
      },
      stackedLine: {
        id: 'chartLine',
        component: (
          <LineChart
            // ref={lineChartRef}
            ref={lineChart}
            height={height}
            // colorArr={BAR_COLORS}
            // legendData={legendData}
            // xAxisData={xAxisData}
            // series={lineSeries}
            // formatterFunc={chartConfig?.barFormatter}
            // setOtherConfigFunc={chartConfig?.setOtherConfigFunc}
            lineOption={lineOption}
            onChartEarliestClick={onChartEarliestClick}
            ifLineChartLegendOn={ifLineChartLegendOn}
            chartType={chartType}
            setLegendSelectedFalse={setLegendSelectedFalse}
          />
        ),
      },
      pie: {
        id: 'chartPie',
        component: (
          <>
            {isHoverPie ? (
              <PieChartHover
                pieOption={pieOption}
                pieData={pieData}
                metricText={metricText}
                startDate={startDate}
                endDate={endDate}
                topChartsType={topChartsType}
                byDevice={byDevice}
                secondTitleRight={secondTitleRight}
                ifHasOtherHeadContent={ifHasOtherHeadContent}
              />
            ) : (
              <PieChart
                ref={pieChart}
                // loading={false}
                // isSort={true}
                // isShowTop5Lines={true}
                // chartData={dataPieArr}
                height={height}
                // colorArr={BAR_COLORS}
                // legendData={legendPieData}
                // formatterFunc={chartConfig.pieFormatter}
                chartType={chartType}
                pieOption={pieOption}
                pieDataHighIndex={pieDataHighIndex}
                // if(isSort) {
                //   // 本期只是做overview的效果给产品看，后面如果全局修改则删除，直接写在配置中
                //   opts.series[0].startAngle =90;
                //   opts.series[0].clockwise =false;
                // }
              />
            )}
          </>
        ),
      },
    };
    return tabsChart;
  }, [props]);

  useImperativeHandle(ref, () => ({
    getBarInstance: () => barChart.current?.instance(),
    getPieInstance: () => pieChart.current?.instance(),
    getLineInstance: () => lineChart.current?.instance(),
  }));

  const getFilterLegendOption = (lineOption: any, selectedNoList: any) => {
    let optionTemp = lineOption;
    if (typeof lineOption === 'object' && lineOption !== null) {
      if (Object.prototype.hasOwnProperty.call(lineOption, 'series')) {
        const legendNames: string[] = Array.isArray(lineOption?.legend?.data)
          ? lineOption.legend.data.map((d: any) => (typeof d === 'string' ? d : d?.name)).filter(Boolean)
          : (lineOption?.series || []).map((s: any) => s?.name).filter(Boolean);
        const selectedMap: Record<string, boolean> = {};
        legendNames.forEach((name) => {
          selectedMap[name] = !selectedNoList?.includes(name);
        });
        optionTemp = {
          ...optionTemp,
          legend: {
            ...optionTemp?.legend,
            selected: selectedMap,
          },
        };
      }
    }
    return optionTemp;
  };

  useEffect(() => {
    // console.log('lineChartOptionRef.current', lineChartOptionRef.current);
    if (
      !isEmpty(lineOption) &&
      (chartType === 'line' || chartType === 'stackedLine')
    ) {
      setStatsChartOption?.(
        getFilterLegendOption(lineOption, legendSelectedFalse),
      );
      // lineChartOptionRef.current = lineOption;
    }
  }, [lineOption, chartType, legendSelectedFalse]);

  useEffect(() => {
    if (
      !isEmpty(lineOption) &&
      (chartType === 'line' || chartType === 'stackedLine') &&
      legendSelectedFalse?.length > 0
    ) {
      setLegendSelectedFalse(getNoSelectedLegend(lineOption));
    }
  }, [ifResetLegend]);

  return (
    <Abnormal
      isLoading={chartLoading}
      isError={isError}
      isNoData={isNodata}
      component={tabsChartArr[chartType]?.component}
      style={{ height: echartHight }}
      nodataMsg={nodataMsg}
    />
  );
};

export default forwardRef(ChartsIndex);
